import { Tooltip, Button, Divider, Typography, Badge, Row, Col, message, Modal } from 'antd';
import { DeleteOutlined, ExclamationCircleFilled, ShoppingCartOutlined } from '@ant-design/icons';
import { RouteNames } from 'src/routes/routeName';
import EmptyBox from '../../empty/EmptyBox';
import TranslationKey from 'src/i18n/translation';
import { useEffect, useState } from 'react';
import { AssetPublishModel } from 'src/models/assetModel';
import { useSessionContext } from 'src/context/session-context';
import _ from 'lodash';
import classStore from 'src/store/classStore';
import { LanguageConstants } from 'src/constant/language';
import GalleryItem from '../../galleryItem/GalleryItem';
import { useNavigate } from 'react-router-dom';
import { SessionStatus } from 'src/constant/sessionStatus';

const { Text } = Typography;

interface CartTooltipProps {
  count: number;
}

export default function CartTooltip({ count }: CartTooltipProps) {
  const [assets, setAssets] = useState<AssetPublishModel[]>([]);
  const { getOrderAssets, setOrderAssets: setOrderAssets, isAuthenticated, countOrder, orderChanged } = useSessionContext();
  const [hasCartItem, setHasCartItem] = useState(false);
  const navigate = useNavigate();
  const { } = useSessionContext();
  const [messageApi, contextHolder] = message.useMessage();
  const { confirm } = Modal;

  type FooterProps = {
    label: string;
    value: string;
    valueColor?: string;
  };

  useEffect(() => {
    onGetOrders();
  }, [orderChanged]);

  const showGrandTotal = (assets: AssetPublishModel[]) => {
    var total = 0;
    var vat = 0;
    var grandTotal = 0;

    if (assets.length === 0) {
      return { total, vat, grandTotal, };
    }

    total = assets.reduce((sum, current) => (sum = sum + current.price), 0);
    vat = (total * 7) / 100;
    grandTotal = total + vat;
    return { total, vat, grandTotal, };
  }

  const onGetOrders = () => {
    let orders = getOrderAssets();
    if (!orders) {
      return;
    }

    const orderGrpItemStd = _(orders)
      .groupBy((item) => {
        return item['id'] + item.standardLicense['id'];
      })
      .map(function (orders, orderid) {
        return { orderid: orderid, orders: orders };
      })
      .value();

    for (let i = 0; i < orders.length; i++) {
      const hasExtendedLicenses =
        orderGrpItemStd.findIndex(
          (g) => g.orderid === orders[i].id + orders[i].standardLicense.id && g.orders.length > 1
        ) > -1;

      if (hasExtendedLicenses) {
        const fisrtExtId = orderGrpItemStd.filter(
          (g) => g.orderid === orders[i].id + orders[i].standardLicense.id && g.orders.length > 0
        )[0].orders[0].extendedLicense.id;

        if (orders[i].extendedLicense.id !== fisrtExtId) {
          orders[i].price = orders[i].extendedLicense.price;
        }
      }
    }

    setAssets(orders);
    setHasCartItem(orders.length > 0)
  };

  const onDelete = (id: string, stdId: string, extId: string) => {
    let orders = getOrderAssets();

    if (!orders) {
      orders = [];
    }

    const oldIndex = orders.findIndex(
      (order) => order.id === id && order.standardLicense.id === stdId && order.extendedLicense.id === extId
    );

    if (oldIndex > -1) {
      orders.splice(oldIndex, 1);
    }

    setOrderAssets(orders);
    onGetOrders();
  };

  const buildFooteritem = ({
    label,
    value,
    valueColor,
  }: FooterProps) => (
    <Row justify="space-between" className="mb-2">
      <Col span={14} style={{ textAlign: 'right' }}>
        <Text strong>
          {label}
        </Text>
      </Col>
      <Col span={10} style={{ textAlign: 'right' }}>
        <Text strong style={{ color: valueColor || '#000' }}>
          {value}
        </Text>
      </Col>
    </Row>
  );

  const buildFooter = () => {
    const {
      total,
      vat,
      grandTotal
    } = showGrandTotal(assets);

    const totalLabel =
      classStore.get(LanguageConstants.key) === LanguageConstants.th
        ? total
          .toLocaleString(undefined, {
            minimumFractionDigits: 2,
          }) + ' ' + TranslationKey('cart.baht')
        : TranslationKey('cart.baht') + ' ' + total
          .toLocaleString(undefined, {
            minimumFractionDigits: 2,
          });

    const vatLabel =
      classStore.get(LanguageConstants.key) === LanguageConstants.th
        ? vat
          .toLocaleString(undefined, {
            minimumFractionDigits: 2,
          }) + ' ' + TranslationKey('cart.baht')
        : TranslationKey('cart.baht') + ' ' + vat
          .toLocaleString(undefined, {
            minimumFractionDigits: 2,
          });

    const grandTotalLabel =
      classStore.get(LanguageConstants.key) === LanguageConstants.th
        ? grandTotal
          .toLocaleString(undefined, {
            minimumFractionDigits: 2,
          }) + ' ' + TranslationKey('cart.baht')
        : TranslationKey('cart.baht') + ' ' + grandTotal
          .toLocaleString(undefined, {
            minimumFractionDigits: 2,
          });

    return (
      <>
        {
          buildFooteritem({
            label: TranslationKey('cart.total'),
            value: totalLabel
          })
        }
        <Divider style={{
          marginTop: '3px',
          marginBottom: '3px',
          borderColor: 'lightgray',
        }} />
        {
          buildFooteritem(
            {
              label: TranslationKey('cart.vat'),
              value: vatLabel
            })
        }
        <Divider style={{
          marginTop: '3px',
          marginBottom: '3px',
          borderColor: 'lightgray',
        }} />
        {buildFooteritem({
          label: TranslationKey('cart.grandTotal'),
          value: grandTotalLabel,
          valueColor: '#FF5501'
        })}
      </>
    );
  }

  const buildHeader = () => {
    return (
      <>
        <Row justify="space-between" className="mt-1 mb-1">
          <Col span={10}>
            <div style={{
              textAlign: 'start',
              paddingLeft: 5,
            }}>
              <Text strong>
                {TranslationKey('cartTooltip.item')}
              </Text>
            </div>
          </Col>
          <Col span={7}>
            <div style={{
              textAlign: 'start',
            }}>
              <Text strong>
                {TranslationKey('cartTooltip.license')}
              </Text>
            </div>
          </Col>
          <Col span={7}>
            <div style={{
              textAlign: 'end',
              paddingRight: 5,
            }}>
              <Text strong>
                {TranslationKey('cartTooltip.price')}
              </Text>
            </div>
          </Col>
        </Row>
      </>
    );
  }

  const onClickItem = (assetId: string) => {
    window.location.href = RouteNames.assetDetail.replace(':id', assetId);
  };

  const buildCartItem = (
    asset: AssetPublishModel,
    assetIndex: number) => {
    return (
      <Row key={`${asset.id}-${asset.standardLicense.id}-${asset.extendedLicense.id}`}
        justify="space-between"
        className="mb-2"
        style={{ backgroundColor: assetIndex % 2 === 0 ? '#f5f5f5' : '#fff' }}>
        <Col span={10} style={{ textAlign: 'start', padding: '5px' }}>
          <GalleryItem
            asset={asset}
            index={assetIndex}
            onClickItem={onClickItem}
          />
        </Col>
        <Col span={7} style={{ textAlign: 'start', padding: '5px' }}>
          <Text className='cart-tooltip-text-sm'>
            {
              asset.standardLicense.price.toLocaleString(
                undefined, { minimumFractionDigits: 0 }) +
              ' ' + asset.standardLicense.name
            }
          </Text>
          <br />
          {
            asset.extendedLicense.price > 0
            &&
            <Text className='cart-tooltip-text-sm'>
              {
                '+ ' +
                asset.extendedLicense.price.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                }) +
                ' ' +
                asset.extendedLicense.name
              }
            </Text>
          }
        </Col>
        <Col span={7} style={{ textAlign: 'end', padding: '5px' }}>
          <div className='cart-tooltip-item-tail'>
            <Text className='cart-tooltip-text-sm'>
              {
                classStore.get(LanguageConstants.key) === LanguageConstants.th
                  ? asset.price.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  }) + ' ' + TranslationKey('cart.baht')
                  : TranslationKey('cart.baht') + ' ' + asset.price.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })
              }
            </Text>
            <Button className='cart-delete-item-button'
              type='default'
              shape='circle'
              size='small'
              icon={<DeleteOutlined />}
              onClick={() =>
                onDelete(
                  asset.id,
                  asset.standardLicense.id,
                  asset.extendedLicense.id)}
            />
          </div>
        </Col>
      </Row>
    );
  }

  const buildBody = () => {
    return (
      <>
        <Divider style={{ marginTop: '3px', marginBottom: '0px', }} />
        <div className='cart-tooltip-body'
          style={{
            height: '100%',
            maxHeight: 375,
            overflowY: 'auto',
          }}>
          {
            assets.map((asset, index) => (
              buildCartItem(asset, index)
            ))
          }
        </div>
        <Divider style={{
          marginTop: '0px',
          marginBottom: '15px',
          borderColor: 'lightgray',
          borderTopWidth: '3px',
        }} />
      </>
    );
  }

  const onCheckout = () => {
    if (assets.length === 0) {
      messageApi.open({
        type: 'warning',
        content: TranslationKey('cart.cartItemIsEmpty'),
      });

      return;
    }

    if (isAuthenticated === SessionStatus.UNAUTHENTICATED) {
      navigate(RouteNames.login);

      return;
    }

    navigate(RouteNames.order);
  };


  const cartContent = (
    hasCartItem
      ?
      <div>
        {buildHeader()}
        {buildBody()}
        {buildFooter()}
        <Row justify='center' className='mt-4 mb-1'>
          <Button
            shape='round'
            type='primary'
            className='btn btn-primary w-100'
            htmlType='submit'
            size='large'
            onClick={() => onCheckout()}
          >
            {TranslationKey('button.checkOut')}
          </Button>
        </Row>
      </div>
      :
      <EmptyBox
        iconSize={75}
        icon={< ShoppingCartOutlined />}
        title={TranslationKey('cart.cartItemIsEmpty')} />
  );

  return (
    <Tooltip
      title={cartContent}
      overlayStyle={{ padding: 0 }}
      placement="bottom"
      color="#fff"
      overlayInnerStyle={{
        width: 400,
        padding: 5,
        borderRadius: 8,
      }}
    >
      <Badge
        count={count}
        offset={[10, 20]}
        style={{ borderColor: '#18191C' }}>
        <ShoppingCartOutlined

          style={{ fontSize: '1.6em' }}
          className='icon-only text-light' />

      </Badge>
    </Tooltip>
  );
}
